/**
 * Post detail screen (route: Post — param { id }).
 *
 * Shows a single post (header) with its comment thread (FlatList body) and a
 * pinned composer at the bottom. The post renders as the list header so it
 * scrolls together with the comments.
 *
 * API endpoints:
 *   GET    /posts/:id                 — post (see normalizePost for shape notes)
 *   GET    /posts/:id/comments        — comment list
 *   POST   /posts/:id/comments        — add comment
 *   POST   / DELETE /posts/:id/like   — like / unlike
 *   POST   / DELETE /posts/:id/repost — repost / undo
 *   POST   / DELETE /posts/:id/bookmark — bookmark / remove
 *   POST   / DELETE /comments/:id/like  — like / unlike a comment
 *
 * Key behavior:
 *   - Like / repost / bookmark on the post are optimistic and fire-and-forget
 *     (no rollback). Comment likes ARE rolled back on failure.
 *   - New comments are appended optimistically from the POST response and the
 *     post's comment counter is bumped locally.
 *   - Backend payloads are camelCase with inconsistent count fields, so all
 *     server rows pass through normalizePost / normalizeComment first.
 *
 * BARE-RN port (P3-2): expo-router Stack.Screen/useRouter/useLocalSearchParams/
 * useFocusEffect → react-navigation (header in navigator; useNavigation/useRoute/
 * useFocusEffect; route.params.id). expo-haptics → services/haptics adapter.
 * @expo/vector-icons → local Icon shim. Engagement/optimistic logic verbatim.
 */
import { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react';
import RichPostText from '../../components/feed/RichPostText';
import { navigate as navRef } from '../../navigation/navigationRef';
import {
  View,
  Text,
  FlatList,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  Share,
  Alert,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { getMutedWords, containsMutedWord } from '../../services/mutedWords';
import { Ionicons } from '../../components/ui/Icon';
import { impact } from '../../services/haptics';
import { api } from '../../services/api';
import { colors, spacing, radius, fonts, layout as layoutTokens, getLayout } from '../../constants/theme';
import { tabularNums } from '../../lib/format';
import ParticleBackground from '../../components/ui/ParticleBackground';
import LoadingState from '../../components/ui/LoadingState';
import { GlassSurface, GlassCard } from '../../components/glass';
import ReportBlockSheet, { ReportTarget } from '../../components/moderation/ReportBlockSheet';
import {
  seedEngagement,
  subscribe as subscribeEngagement,
  applyLike,
  applyRepost,
  applyBookmark,
  type PostEngagement,
} from '../../services/postEngagement';
import type { RootStackParamList } from '../../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList>;
type PostRoute = RouteProp<RootStackParamList, 'Post'>;

interface Post {
  id: string;
  content: string;
  author: {
    username: string;
    displayName: string;
    avatar: string;
    verified?: boolean;
  };
  createdAt: string;
  likes: number;
  comments: number;
  reposts: number;
  liked?: boolean;
  bookmarked?: boolean;
  reposted?: boolean;
  media?: string[];
}

interface Comment {
  id: string;
  content: string;
  author: {
    username: string;
    displayName: string;
    avatar: string;
  };
  createdAt: string;
  likes: number;
  liked?: boolean;
}

// Backend post (GET /posts/:id) returns the row + User relation (camelCase) but
// no _count; scalar columns commentCount/upvotes/repostCount/score are present.
function normalizePost(raw: any): Post {
  const u = raw?.User || {};
  return {
    id: raw?.id,
    content: raw?.content ?? '',
    author: {
      username: u.username ?? 'user',
      displayName: u.displayName ?? u.display_name ?? '',
      avatar: u.avatar ?? u.avatar_url ?? '',
      verified: u.isVerified ?? false,
    },
    createdAt: raw?.createdAt ?? raw?.created_at ?? '',
    likes: raw?._count?.Vote ?? raw?.upvotes ?? 0,
    comments: raw?._count?.Comment ?? raw?.commentCount ?? 0,
    reposts: raw?.repostCount ?? 0,
    liked: raw?.liked ?? false,
    bookmarked: raw?.bookmarked ?? false,
    reposted: raw?.reposted ?? false,
    media: Array.isArray(raw?.media) ? raw.media : [],
  };
}

// Backend comment returns User (camelCase) + _count.Vote (single comment from
// POST has no _count).
function normalizeComment(raw: any): Comment {
  const u = raw?.User || {};
  return {
    id: raw?.id,
    content: raw?.content ?? '',
    author: {
      username: u.username ?? 'user',
      displayName: u.displayName ?? u.display_name ?? '',
      avatar: u.avatar ?? u.avatar_url ?? '',
    },
    createdAt: raw?.createdAt ?? raw?.created_at ?? '',
    likes: raw?.likes ?? raw?._count?.Like ?? raw?._count?.Vote ?? 0,
    liked: raw?.liked ?? false,
  };
}

// Comment page size. The comments endpoint pages by `limit` (no cursor), so we
// load this many at a time and grow the limit on "load more" (see fetchData).
const COMMENTS_PAGE_SIZE = 20;

export default function PostDetailScreen() {
  const route = useRoute<PostRoute>();
  const { id } = route.params ?? ({} as PostRoute['params']);
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const inputRef = useRef<TextInput>(null);
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;

  // Self-manage the chrome: hide the opaque native stack header and render an
  // in-screen translucent header (skill §12) so content scrolls under the aurora.
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  // Content clears the floating translucent header (header row + safe-area top).
  const headerPad = insets.top + layoutTokens.headerHeight;

  // --- State ---
  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  // ── Comment pagination ──────────────────────────────────────────────────────
  // The API's GET /posts/:id/comments has no cursor/offset — it accepts `limit`
  // (top-ranked by score). So we page by GROWING the limit (COMMENTS_PAGE_SIZE at
  // a time) and re-requesting; the response is stable/ordered so we just replace
  // the list. `hasMore` is inferred from "a full page came back" — the same
  // fallback the feed's pagination uses (items.length >= PAGE_SIZE). `loadingMore`
  // drives the footer spinner; the ref guards overlapping onEndReached callbacks.
  const [commentLimit, setCommentLimit] = useState(COMMENTS_PAGE_SIZE);
  const [hasMoreComments, setHasMoreComments] = useState(false);
  const [loadingMoreComments, setLoadingMoreComments] = useState(false);
  const loadingMoreCommentsRef = useRef(false);
  // Distinguish a genuine 404 (the post really doesn't exist → permanent "not
  // found") from a transient network/fetch failure (→ retryable error state).
  const [loadError, setLoadError] = useState<'none' | 'notFound' | 'transient'>('none');
  // Shared engagement (like/repost/bookmark) — the SAME store the feed uses, so a
  // like here reflects in the feed card and live socket echoes reconcile counts.
  const [eng, setEng] = useState<PostEngagement | null>(null);
  // Subscribe for live engagement updates for this post id.
  useEffect(() => {
    if (!id) return;
    return subscribeEngagement(String(id), setEng);
  }, [id]);
  // Device-local muted words; comments containing one are hidden. Reloaded on focus.
  const [mutedWords, setMutedWords] = useState<string[]>([]);
  useFocusEffect(
    useCallback(() => {
      let active = true;
      getMutedWords().then((w) => { if (active) setMutedWords(w); });
      return () => { active = false; };
    }, [])
  );
  const [commentText, setCommentText] = useState('');
  const [sending, setSending] = useState(false);
  // Trust & Safety (Apple 1.2): the report/block sheet target (post or comment).
  const [tsTarget, setTsTarget] = useState<ReportTarget | null>(null);
  // Current user's username — used to gate the author-only "Delete post" action
  // in the post kebab (an author reports/blocks nobody; they get Delete instead).
  const [meUsername, setMeUsername] = useState<string | undefined>(undefined);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api<{ user?: any }>('/auth/me');
        const me = res.success ? (res.data as any)?.user ?? res.data : null;
        if (active && me) setMeUsername(me.username ?? undefined);
      } catch { /* non-fatal: no delete affordance without a confirmed author match */ }
    })();
    return () => { active = false; };
  }, []);
  // Guard so a double-confirmed delete can't fire the DELETE twice.
  const deletingRef = useRef(false);

  // --- Effects ---
  useEffect(() => {
    fetchData();
  }, [id]);

  // Load post + comments together. A genuine 404 → permanent "not found"; any
  // other failure (network/timeout/5xx) → retryable transient error state.
  async function fetchData() {
    setLoading(true);
    setLoadError('none');
    // A fresh load resets comment paging back to the first page.
    setCommentLimit(COMMENTS_PAGE_SIZE);
    setLoadingMoreComments(false);
    loadingMoreCommentsRef.current = false;
    try {
      const [postRes, commentsRes] = await Promise.all([
        api<any>(`/posts/${id}`),
        api<any[]>(`/posts/${id}/comments?limit=${COMMENTS_PAGE_SIZE}`),
      ]);
      if (postRes.success && postRes.data) {
        const np = normalizePost(postRes.data);
        setPost(np);
        // Seed shared engagement so the feed and this screen share one truth.
        setEng(
          seedEngagement(np.id, {
            liked: np.liked ?? false,
            likes: np.likes ?? 0,
            reposted: np.reposted ?? false,
            reposts: np.reposts ?? 0,
            bookmarked: np.bookmarked ?? false,
          })
        );
      } else {
        // A real 404 means the post is gone; anything else is transient/retryable.
        setLoadError(/404|not found/i.test(postRes.error || '') ? 'notFound' : 'transient');
      }
      if (commentsRes.success) {
        // Reset even on empty/failed-shape so a transient failure never leaves
        // stale comments from a previous post on screen.
        const rows = Array.isArray(commentsRes.data) ? commentsRes.data : [];
        setComments(rows.map(normalizeComment));
        // A full page came back → there may be more (grow the limit to fetch them).
        setHasMoreComments(rows.length >= COMMENTS_PAGE_SIZE);
      }
    } catch {
      // Network error — retryable transient state.
      setLoadError('transient');
    } finally {
      setLoading(false);
    }
  }

  // Load the NEXT page of comments by growing the requested limit and replacing
  // the list with the larger, still-ordered result (the endpoint has no cursor).
  // Guarded so overlapping onEndReached callbacks can't double-fetch; a failed
  // page silently stops (leaves hasMore as-is so scrolling again retries it).
  async function loadMoreComments() {
    if (loadingMoreCommentsRef.current || !hasMoreComments || loading) return;
    loadingMoreCommentsRef.current = true;
    setLoadingMoreComments(true);
    const nextLimit = commentLimit + COMMENTS_PAGE_SIZE;
    try {
      const res = await api<any[]>(`/posts/${id}/comments?limit=${nextLimit}`);
      if (res.success) {
        const rows = Array.isArray(res.data) ? res.data : [];
        setComments(rows.map(normalizeComment));
        setCommentLimit(nextLimit);
        // More remain only if the server filled the whole grown page.
        setHasMoreComments(rows.length >= nextLimit);
      }
    } catch {
      // Transient — leave hasMoreComments so scrolling again retries the page.
    } finally {
      setLoadingMoreComments(false);
      loadingMoreCommentsRef.current = false;
    }
  }

  // --- Handlers ---
  // Optimistic post like via the SHARED engagement store (rollback on failure),
  // so the feed card stays in sync and a live socket echo can reconcile the count.
  const likingRef = useRef(false);
  const repostingRef = useRef(false);
  const bookmarkingRef = useRef(false);
  // Per-comment in-flight guard (keyed by comment id) — mirrors the post-level
  // *Ref guards above so rapid taps on a comment heart can't fire overlapping
  // POST/DELETE requests off stale state.
  const commentLikingRef = useRef<Record<string, boolean>>({});

  // A failed like/repost/bookmark rolls back optimistically — but a silent
  // bounce-back reads as a glitch. Surface ONE lightweight, non-spammy Alert on
  // rollback (skip the silent auth-expiry sentinels, which route to Login).
  function notifyActionFailed(res: { silent?: boolean }) {
    if (res?.silent) return;
    Alert.alert('Something went wrong', 'That action didn’t go through. Please try again.');
  }

  async function handleLike() {
    if (!eng || likingRef.current) return;
    likingRef.current = true;
    impact('light');
    const wasLiked = eng.liked;
    const rollback = applyLike(String(id), !wasLiked);
    const res = await api(`/posts/${id}/like`, { method: wasLiked ? 'DELETE' : 'POST' });
    if (!res.success) { rollback(); notifyActionFailed(res); }
    likingRef.current = false;
  }

  async function handleRepost() {
    if (!eng || repostingRef.current) return;
    repostingRef.current = true;
    const was = eng.reposted;
    const rollback = applyRepost(String(id), !was);
    const res = await api(`/posts/${id}/repost`, { method: was ? 'DELETE' : 'POST' });
    if (!res.success) { rollback(); notifyActionFailed(res); }
    repostingRef.current = false;
  }

  async function handleBookmark() {
    if (!eng || bookmarkingRef.current) return;
    bookmarkingRef.current = true;
    impact('medium');
    const was = eng.bookmarked;
    const rollback = applyBookmark(String(id), !was);
    const res = await api(`/posts/${id}/bookmark`, { method: was ? 'DELETE' : 'POST' });
    if (!res.success) { rollback(); notifyActionFailed(res); }
    bookmarkingRef.current = false;
  }

  // Native share sheet with a canonical web URL for the post.
  async function handleShare() {
    try {
      await Share.share({ message: `Check out this post on CRYB: https://platform.cryb.ai/post/${id}` });
    } catch {}
  }

  // Post a comment. `sending` guards against double-submit. On success the
  // server's normalized comment is prepended and the post's comment count bumped
  // and the composer is cleared. On failure the text is KEPT (so the user doesn't
  // lose what they typed) and an Alert surfaces the error.
  async function handleSendComment() {
    if (!commentText.trim() || sending) return;
    setSending(true);
    const res = await api<any>(`/posts/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content: commentText.trim() }),
    });
    if (res.success && res.data) {
      setComments(prev => [normalizeComment(res.data), ...prev]);
      if (post) setPost({ ...post, comments: (post.comments ?? 0) + 1 });
      // Grow the page window so a later load-more refetch doesn't drop this new one.
      setCommentLimit(l => l + 1);
      setCommentText('');
    } else if (!res.silent) {
      // Keep the composer text and let the user retry.
      Alert.alert('Error', res.error || 'Failed to post comment. Please try again.');
    }
    setSending(false);
  }

  // Optimistic comment like WITH rollback. Unlike the post-level toggles, a
  // failed request here reverts the flag and count so the thread stays accurate.
  async function handleLikeComment(commentId: string) {
    // Coalesce rapid taps on THIS comment's heart (per-id guard) — matches the
    // post-level *Ref guards so overlapping requests off stale state can't fire.
    if (commentLikingRef.current[commentId]) return;
    commentLikingRef.current[commentId] = true;
    // Capture liked state BEFORE the optimistic update to avoid stale closure
    const target = comments.find(c => c.id === commentId);
    const wasLiked = target?.liked ?? false;
    setComments(prev =>
      prev.map(c =>
        c.id === commentId
          ? { ...c, liked: !wasLiked, likes: (c.likes ?? 0) + (wasLiked ? -1 : 1) }
          : c
      )
    );
    const res = await api(`/comments/${commentId}/like`, { method: wasLiked ? 'DELETE' : 'POST' });
    if (!res.success) {
      // Rollback on failure
      setComments(prev =>
        prev.map(c =>
          c.id === commentId
            ? { ...c, liked: wasLiked, likes: (c.likes ?? 0) + (wasLiked ? 1 : -1) }
            : c
        )
      );
    }
    commentLikingRef.current[commentId] = false;
  }

  // --- Helpers / render ---
  // Compact relative timestamp (now / 12m / 5h / 3d); '' for missing/bad dates.
  function formatTime(dateStr?: string | null): string {
    if (!dateStr) return '';
    const ts = new Date(dateStr).getTime();
    if (isNaN(ts)) return '';
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  }

  // Single comment row (avatar links to author profile, heart toggles the like).
  // Report a comment / block its author (Apple 1.2 — flag objectionable content).
  // Open the shared glass report/block sheet for a comment (reason picker +
  // block/mute its author + confirmation). Apple 1.2 UGC requirement.
  function showCommentOptions(commentId: string, authorUsername: string) {
    setTsTarget({
      type: 'comment',
      contentId: commentId,
      authorUsername,
      authorLabel: `@${authorUsername}`,
    });
  }

  function renderComment({ item }: { item: Comment }) {
    const author = item?.author || { username: 'user', displayName: 'User', avatar: '' };
    const likes = item?.likes ?? 0;
    return (
      <GlassCard style={styles.commentRow} contentStyle={styles.commentRowContent} padding={0}>
        <TouchableOpacity
          onPress={() => navigation.navigate('UserProfile', { username: author.username })}
          accessibilityRole="button"
          accessibilityLabel={`View ${author.displayName || author.username || 'user'}'s profile`}
        >
          {author.avatar && author.avatar.length > 0 ? (
            <Image source={{ uri: author.avatar }} style={styles.commentAvatar} />
          ) : (
            <View style={[styles.commentAvatar, { backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center' }]}>
              <Text style={{ color: '#fff', fontWeight: '700', fontSize: 12 }}>{(author.displayName || author.username || '?').charAt(0).toUpperCase()}</Text>
            </View>
          )}
        </TouchableOpacity>
        <View style={styles.commentBody}>
          <View style={styles.commentHeader}>
            <Text style={styles.commentName} numberOfLines={1}>{author.displayName || author.username || 'User'}</Text>
            <Text style={styles.commentUsername} numberOfLines={1}>@{author.username || 'user'}</Text>
            <Text style={styles.commentTime}>{formatTime(item?.createdAt)}</Text>
            <TouchableOpacity
              style={styles.commentOptions}
              onPress={() => showCommentOptions(item.id, author.username || 'user')}
              accessibilityRole="button"
              accessibilityLabel="Comment options"
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            >
              <Ionicons name="ellipsis-horizontal" size={14} color={colors.faint} />
            </TouchableOpacity>
          </View>
          <Text style={styles.commentContent}>{item?.content || ''}</Text>
          <TouchableOpacity
            style={styles.commentLikeRow}
            onPress={() => handleLikeComment(item.id)}
            activeOpacity={0.6}
            accessibilityRole="button"
            accessibilityLabel={item?.liked ? 'Unlike comment' : 'Like comment'}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons
              name={item?.liked ? 'heart' : 'heart-outline'}
              size={14}
              color={item?.liked ? colors.error : colors.muted}
            />
            {likes > 0 && (
              <Text style={[styles.commentLikeCount, tabularNums, item?.liked && { color: colors.error }]}>
                {likes}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </GlassCard>
    );
  }

  // Delete the viewer's OWN post (author-only). Confirms first, calls DELETE
  // /posts/:id, then leaves the detail screen on success. On failure surfaces a
  // non-silent Alert and stays put. Guarded against a double-fire.
  async function handleDeletePost() {
    if (deletingRef.current) return;
    deletingRef.current = true;
    const res = await api(`/posts/${id}`, { method: 'DELETE' });
    if (res.success) {
      navigation.goBack();
    } else {
      if (!res.silent) {
        Alert.alert('Couldn’t delete post', res.error || 'Please try again.');
      }
      deletingRef.current = false;
    }
  }

  function confirmDeletePost() {
    Alert.alert(
      'Delete post?',
      'This can’t be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: handleDeletePost },
      ],
      { cancelable: true }
    );
  }

  // Post kebab. The AUTHOR gets a "Delete post" action (their own post — reporting
  // or blocking themselves is meaningless); everyone else gets the Apple 1.2
  // report/block sheet (flag objectionable content + block abusive users).
  function showPostOptions(authorUsername: string) {
    const isAuthor = !!meUsername && authorUsername.toLowerCase() === meUsername.toLowerCase();
    if (isAuthor) {
      Alert.alert(
        'Post options',
        undefined,
        [
          { text: 'Delete post', style: 'destructive', onPress: confirmDeletePost },
          { text: 'Cancel', style: 'cancel' },
        ],
        { cancelable: true }
      );
      return;
    }
    setTsTarget({
      type: 'post',
      contentId: String(id),
      authorUsername,
      authorLabel: `@${authorUsername}`,
    });
  }

  // List header: the post itself — author, content, media, stats and action bar.
  // Rendered inside the comments FlatList so post + thread scroll as one.
  function renderHeader() {
    if (!post) return null;
    const author = post.author || { username: 'user', displayName: 'User', avatar: '' };
    const media = Array.isArray(post.media) ? post.media.filter(Boolean) : [];
    // Engagement display comes from the shared store (live), falling back to the
    // post's own server values before the store has seeded.
    const likes = eng?.likes ?? post.likes ?? 0;
    const reposts = eng?.reposts ?? post.reposts ?? 0;
    const isLiked = eng?.liked ?? post.liked ?? false;
    const isReposted = eng?.reposted ?? post.reposted ?? false;
    const isBookmarked = eng?.bookmarked ?? post.bookmarked ?? false;
    return (
      <GlassCard style={styles.postContainer} padding={spacing.lg}>
        {/* Author row */}
        <TouchableOpacity
          style={styles.authorRow}
          onPress={() => navigation.navigate('UserProfile', { username: author.username })}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={`View ${author.displayName || author.username || 'user'}'s profile`}
        >
          {author.avatar && author.avatar.length > 0 ? (
            <Image source={{ uri: author.avatar }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatar, { backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center' }]}>
              <Text style={{ color: '#fff', fontWeight: '700', fontSize: 18 }}>{(author.displayName || author.username || '?').charAt(0).toUpperCase()}</Text>
            </View>
          )}
          <View style={styles.authorInfo}>
            <View style={styles.nameRow}>
              <Text style={styles.displayName} numberOfLines={1}>{author.displayName || author.username || 'User'}</Text>
              {author.verified && (
                <Ionicons name="checkmark-circle" size={16} color={colors.primary} />
              )}
            </View>
            <Text style={styles.username} numberOfLines={1}>@{author.username || 'user'}</Text>
          </View>
          <TouchableOpacity
            activeOpacity={0.6}
            onPress={() => showPostOptions(author.username || 'user')}
            accessibilityRole="button"
            accessibilityLabel="Post options"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons name="ellipsis-horizontal" size={20} color={colors.muted} />
          </TouchableOpacity>
        </TouchableOpacity>

        {/* Content — $CASHTAGS tappable → CoinDetail. */}
        <RichPostText
          content={post.content || ''}
          style={styles.postContent}
          onCashtag={(symbol) => navRef('CoinDetail', { symbol })}
        />

        {/* Media */}
        {media.length > 0 && (
          <View style={styles.mediaContainer}>
            {media.map((url, i) => (
              <Image key={`${url}-${i}`} source={{ uri: url }} style={styles.mediaImage} resizeMode="cover" />
            ))}
          </View>
        )}

        {/* Timestamp */}
        <Text style={styles.timestamp}>
          {post.createdAt
            ? (() => {
                const d = new Date(post.createdAt);
                return isNaN(d.getTime())
                  ? ''
                  : d.toLocaleString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    });
              })()
            : ''}
        </Text>

        {/* Stats */}
        <View style={styles.statsRow}>
          <Text style={styles.statText}>
            <Text style={[styles.statBold, tabularNums]}>{reposts}</Text> Reposts
          </Text>
          <Text style={styles.statText}>
            <Text style={[styles.statBold, tabularNums]}>{likes}</Text> Likes
          </Text>
          <Text style={styles.statText}>
            <Text style={[styles.statBold, tabularNums]}>{post.comments ?? 0}</Text> Comments
          </Text>
        </View>

        {/* Action bar */}
        <View style={styles.actionBar}>
          <TouchableOpacity
            style={styles.actionBtn}
            onPress={() => inputRef.current?.focus()}
            accessibilityRole="button"
            accessibilityLabel="Comment"
          >
            <Ionicons name="chatbubble-outline" size={20} color={colors.muted} />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionBtn}
            onPress={handleRepost}
            accessibilityRole="button"
            accessibilityLabel={isReposted ? 'Undo repost' : 'Repost'}
          >
            <Ionicons
              name="repeat-outline"
              size={22}
              color={isReposted ? colors.success : colors.muted}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionBtn}
            onPress={handleLike}
            accessibilityRole="button"
            accessibilityLabel={isLiked ? 'Unlike' : 'Like'}
          >
            <Ionicons
              name={isLiked ? 'heart' : 'heart-outline'}
              size={20}
              color={isLiked ? colors.error : colors.muted}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionBtn}
            onPress={handleBookmark}
            accessibilityRole="button"
            accessibilityLabel={isBookmarked ? 'Remove bookmark' : 'Bookmark'}
          >
            <Ionicons
              name={isBookmarked ? 'bookmark' : 'bookmark-outline'}
              size={20}
              color={isBookmarked ? colors.primary : colors.muted}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionBtn}
            onPress={handleShare}
            accessibilityRole="button"
            accessibilityLabel="Share"
          >
            <Ionicons name="share-outline" size={20} color={colors.muted} />
          </TouchableOpacity>
        </View>

        {/* Comments header */}
        <View style={styles.commentsHeader}>
          <Text style={styles.commentsTitle}>Comments</Text>
        </View>
      </GlassCard>
    );
  }

  // In-screen translucent header (skill §12): back + title + share, floating over
  // the aurora with content scrolling underneath. No opaque top bar, no gap.
  function ScreenHeader() {
    return (
      <GlassSurface
        variant="bar"
        radius={0}
        sheen={false}
        shadow={false}
        style={[styles.header, { paddingTop: insets.top }]}
        contentStyle={styles.headerContent}
      >
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.headerBtn}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Ionicons name="chevron-back" size={24} color={colors.ink} />
        </TouchableOpacity>
        <Text style={styles.headerTitle} numberOfLines={1}>Post</Text>
        <TouchableOpacity
          onPress={handleShare}
          style={styles.headerBtn}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityLabel="Share"
        >
          <Ionicons name="share-outline" size={20} color={colors.ink} />
        </TouchableOpacity>
      </GlassSurface>
    );
  }

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground />
        <ScreenHeader />
        <LoadingState message="Loading…" onRetry={fetchData} />
      </View>
    );
  }

  if (!post) {
    // A transient fetch failure gets a Retry (re-runs fetchData); a genuine 404
    // gets the permanent "Post not found" with Go back.
    const isTransient = loadError === 'transient';
    return (
      <View style={styles.screen}>
        <ParticleBackground />
        <ScreenHeader />
        <View style={styles.loader}>
          <Text style={styles.errorText}>
            {isTransient ? "Couldn't load this post" : 'Post not found'}
          </Text>
          {isTransient ? (
            <TouchableOpacity onPress={fetchData} accessibilityRole="button" accessibilityLabel="Retry">
              <Text style={styles.backLink}>Retry</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity onPress={() => navigation.goBack()} accessibilityRole="button" accessibilityLabel="Go back">
              <Text style={styles.backLink}>Go back</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.screen}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={90}
    >
      <ParticleBackground />
      <FlatList
        data={(Array.isArray(comments) ? comments : []).filter((c) => !containsMutedWord(c?.content, mutedWords))}
        keyExtractor={(item: any, index) => item?.id ?? `${item?.author?.username ?? 'c'}-${item?.createdAt ?? index}`}
        renderItem={renderComment}
        ListHeaderComponent={renderHeader}
        ListEmptyComponent={
          <Text style={styles.emptyText}>No comments yet. Be the first!</Text>
        }
        ListFooterComponent={
          loadingMoreComments ? (
            <View style={styles.commentsFooter}>
              <ActivityIndicator size="small" color={colors.primary} />
            </View>
          ) : null
        }
        onEndReached={loadMoreComments}
        onEndReachedThreshold={0.5}
        contentContainerStyle={[styles.listContent, { paddingTop: headerPad }, containerStyle]}
        showsVerticalScrollIndicator={false}
        initialNumToRender={12}
        maxToRenderPerBatch={10}
        windowSize={11}
        removeClippedSubviews
      />
      <ScreenHeader />

      {/* Comment input — translucent glass composer floating over the aurora. */}
      <GlassSurface
        variant="bar"
        radius={0}
        sheen={false}
        style={[styles.inputBar, containerStyle]}
        contentStyle={[styles.inputBarContent, { paddingBottom: spacing.md + insets.bottom }]}
      >
        <TextInput
          ref={inputRef}
          style={styles.commentInput}
          placeholder="Write a comment..."
          placeholderTextColor={colors.muted}
          value={commentText}
          onChangeText={setCommentText}
          multiline
          maxLength={500}
        />
        <TouchableOpacity
          style={[styles.sendBtn, !commentText.trim() && styles.sendBtnDisabled]}
          onPress={handleSendComment}
          disabled={!commentText.trim() || sending}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Send comment"
        >
          {sending ? (
            <ActivityIndicator size="small" color={colors.white} />
          ) : (
            <Ionicons name="send" size={18} color={colors.white} />
          )}
        </TouchableOpacity>
      </GlassSurface>
      <ReportBlockSheet
        visible={!!tsTarget}
        target={tsTarget}
        onClose={() => setTsTarget(null)}
        onBlocked={(t) => {
          setTsTarget(null);
          // Blocking the post's author hides the post → leave the detail screen.
          if (t.type === 'post') navigation.goBack();
        }}
      />
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  // In-screen translucent header (floats over the aurora; content scrolls under).
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    // Hairline bottom edge so the floating glass bar reads as a distinct layer
    // over the scrolling thread (#68 — match the other screens' header depth).
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
  },
  headerContent: {
    height: layoutTokens.headerHeight + spacing.xs,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.sm,
  },
  headerBtn: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 17,
    color: colors.ink,
    letterSpacing: -0.2,
    ...fonts.bold,
  },
  loader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
    color: colors.ink2,
    ...fonts.medium,
  },
  backLink: {
    fontSize: 15,
    color: colors.primary,
    marginTop: spacing.md,
    ...fonts.semibold,
  },
  listContent: {
    paddingBottom: spacing.lg,
  },
  postContainer: {
    marginHorizontal: spacing.lg,
    marginTop: spacing.md,
  },
  authorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.faint,
  },
  authorInfo: {
    flex: 1,
    marginLeft: spacing.md,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  displayName: {
    fontSize: 16,
    color: colors.ink,
    letterSpacing: -0.2,
    ...fonts.bold,
  },
  username: {
    fontSize: 13,
    color: colors.muted,
    marginTop: 1,
    ...fonts.regular,
  },
  postContent: {
    fontSize: 17,
    lineHeight: 24,
    color: colors.ink,
    letterSpacing: -0.1,
    ...fonts.regular,
  },
  mediaContainer: {
    marginTop: spacing.md,
    borderRadius: radius.md,
    overflow: 'hidden',
  },
  mediaImage: {
    width: '100%',
    height: 200,
    borderRadius: radius.md,
    marginBottom: spacing.xs,
  },
  timestamp: {
    fontSize: 13,
    color: colors.muted,
    marginTop: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: 'rgba(26,26,46,0.08)',
    ...fonts.regular,
  },
  statsRow: {
    flexDirection: 'row',
    gap: spacing.xl,
    paddingVertical: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: 'rgba(26,26,46,0.08)',
  },
  statText: {
    fontSize: 14,
    color: colors.muted,
    ...fonts.regular,
  },
  statBold: {
    color: colors.ink,
    ...fonts.bold,
  },
  actionBar: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: 'rgba(26,26,46,0.08)',
  },
  actionBtn: {
    padding: spacing.sm,
  },
  commentsHeader: {
    marginTop: spacing.lg,
  },
  commentsTitle: {
    fontSize: 16,
    color: colors.ink,
    letterSpacing: -0.2,
    ...fonts.bold,
  },
  commentRow: {
    marginHorizontal: spacing.lg,
    marginTop: spacing.sm,
  },
  commentRowContent: {
    flexDirection: 'row',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  commentAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.faint,
  },
  commentBody: {
    flex: 1,
    marginLeft: spacing.md,
  },
  commentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: 2,
  },
  commentName: {
    fontSize: 14,
    color: colors.ink,
    ...fonts.semibold,
  },
  commentUsername: {
    fontSize: 13,
    color: colors.muted,
    ...fonts.regular,
  },
  commentTime: {
    fontSize: 12,
    color: colors.faint,
    ...fonts.regular,
  },
  commentOptions: {
    marginLeft: 'auto',
    paddingLeft: spacing.sm,
  },
  commentContent: {
    fontSize: 14,
    lineHeight: 20,
    color: colors.ink,
    ...fonts.regular,
  },
  commentLikeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  commentLikeCount: {
    fontSize: 12,
    color: colors.muted,
    ...fonts.regular,
  },
  emptyText: {
    textAlign: 'center',
    color: colors.muted,
    fontSize: 14,
    marginTop: spacing.xxxl,
    ...fonts.regular,
  },
  commentsFooter: {
    paddingVertical: spacing.lg,
    alignItems: 'center',
  },
  inputBar: {},
  inputBarContent: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.md,
    gap: spacing.sm,
  },
  commentInput: {
    flex: 1,
    minHeight: 40,
    maxHeight: 100,
    backgroundColor: 'rgba(255,255,255,0.55)',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(26,26,46,0.08)',
    borderRadius: radius.xl,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    fontSize: 15,
    color: colors.ink,
    ...fonts.regular,
  },
  sendBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sendBtnDisabled: {
    opacity: 0.4,
  },
});
